import type { MouseEventHandler } from 'react';
import type { MTextBlockBasicAlignment } from '../../molecules/MTextBlockBasic/MTextBlockBasic.types';
import type { AEyebrowVariant } from '../../atoms/AEyebrow/AEyebrow.types';
import type { ASubheaderSize, ASubheaderTag } from '../../atoms/ASubheader/ASubheader.types';
import type { ABodyColor, ABodySize } from '../../atoms/ABody/ABody.types';
import type { AIconSize } from '../../atoms/AIcon/AIcon.types';
import type { MTagType } from '../../molecules/MTag/MTag.types';
import type { MButtonType, MButtonSize, MButtonIconPlacement } from '../../molecules/MButton/MButton.types';
import type { MButtonIconState } from '../../molecules/MButtonIcon/MButtonIcon.types';
import type { AScrimGradientDirection } from '../../atoms/AScrimGradient/AScrimGradient.types';
import type { TrackingPropsInterface } from '../../utils/enums/enums';
import type { ALabelSize } from '../../atoms/ALabel/ALabel.types';

export enum OCardLayeredStandardCTAStyle {
  Button = 'Button',
  Arrow = 'Arrow',
}

export enum OCardLayeredStandardAspectRatio {
  TALL_1_2 = '1:2',
  WIDE_3_2 = '3:2',
  TALL_2_3 = '2:3',
  TALL_9_16 = '9:16',
  SQUARE_1_1 = '1:1',
  WIDE_16_9 = '16:9',
}

export interface OCardLayeredStandardImageData {
  damPath?: string;
  assetPath?: string;
  altText?: string;
  dynamic?: boolean;
  enableSmartCrop?: boolean;
  renditions?: Array<{
    renditionPath: string;
    mediaValue: string;
    mediaQuery: string;
  }>;
  renditionUrl?: string | null;
}

export interface OCardLayeredStandardAImage {
  image?: OCardLayeredStandardImageData;
}

export interface OCardLayeredStandardAScrimGradient {
  direction?: AScrimGradientDirection;
}

export interface OCardLayeredStandardAEyebrow {
  eyebrowText?: string;
  variant?: AEyebrowVariant;
}

export interface OCardLayeredStandardASubheader {
  text?: string;
  size?: ASubheaderSize;
  element?: ASubheaderTag;
}

export interface OCardLayeredStandardABody {
  bodyText?: string;
  color?: ABodyColor;
  size?: ABodySize;
}

export interface OCardLayeredStandardMTextBlockBasic {
  alignment?: MTextBlockBasicAlignment;
  aEyebrow?: OCardLayeredStandardAEyebrow;
  aSubheader?: OCardLayeredStandardASubheader;
  aBody?: OCardLayeredStandardABody;
}

export interface OCardLayeredStandardAIcon {
  icon: string;
  size?: AIconSize;
}

export interface OCardLayeredStandardMTagALabel {
  text?: string;
  size?: ALabelSize;
}

export interface OCardLayeredStandardMTag {
  type?: MTagType;
  aLabel?: OCardLayeredStandardMTagALabel;
  aIcon?: OCardLayeredStandardAIcon;
}

export interface OCardLayeredStandardMButton {
  /** AEM-authored field name. Takes precedence over the legacy `type` prop. */
  buttonType?: MButtonType | string;
  /** @deprecated AEM dialogs author this as `buttonType`. Use `buttonType` instead. */
  type?: MButtonType;
  size?: MButtonSize;
  buttonText?: string;
  icon?: MButtonIconPlacement;
  ctaLink?: string;
  openInANewTab?: boolean | string;
  aIcon?: OCardLayeredStandardAIcon;
}

export interface OCardLayeredStandardMButtonIcon {
  aIcon?: OCardLayeredStandardAIcon;
  state?: MButtonIconState;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  'aria-label'?: string;
}

export interface OCardLayeredStandardALogo32 {
  brand?: string;
  color?: string;
  logopath?: string;
}

export interface OCardLayeredStandardMLogoContainer {
  aLogo32?: OCardLayeredStandardALogo32;
}

export interface OCardLayeredStandardTrackingProperties extends TrackingPropsInterface {
  trackingDescription?: string;
  location?: string;
  clickTrack?: boolean;
  impressionTrack?: boolean;
}

export interface OCardLayeredStandardProps {
  ctaLink?: string;
  /** AEM-authored value (`1:1`, `16:9`, `3:2`, `2:3`, `9:16`, `1:2`). */
  aspectRatio?: OCardLayeredStandardAspectRatio | string;
  /** AEM-authored value; normalized in the component (`Button` | `Arrow`). */
  ctaStyle?: OCardLayeredStandardCTAStyle | string;
  cTAStyle?: OCardLayeredStandardCTAStyle | string;
  aImage?: OCardLayeredStandardAImage;
  aScrimGradient?: OCardLayeredStandardAScrimGradient;
  mLogoContainer?: OCardLayeredStandardMLogoContainer;
  /** @deprecated Use `mLogoContainer.aLogo32` instead. */
  aLogo32?: OCardLayeredStandardALogo32;
  mTextBlockBasic?: OCardLayeredStandardMTextBlockBasic;
  aIcon?: OCardLayeredStandardAIcon;
  mTag?: OCardLayeredStandardMTag;
  mButton?: OCardLayeredStandardMButton;
  mButton2?: OCardLayeredStandardMButton;
  mButtonIcon?: OCardLayeredStandardMButtonIcon;
  trackingProperties?: OCardLayeredStandardTrackingProperties;
  className?: string;
}
